/**
 * ЛОГИКА ОПЫТА И УРОВНЕЙ НА ВИТРИНЕ · Ф2.5.
 *
 * ⛔ Витрина НЕ считает ни опыта, ни уровня: всё приходит готовым из ядра
 * (`core/gamify.py` выводит их из журнала `events`). Здесь живёт только ПОДАЧА —
 * геометрия кольца, падежи и подписи, — и живёт отдельным модулем именно потому,
 * что иначе её нечем проверить запуском:
 *
 *   cd Lang/web && node tools/check-xp.mjs
 *
 * Проверка заодно сверяет витрину С ЯДРОМ живьём (зовёт python и сравнивает список
 * героев и потолок уровней), чтобы «12 уровней» на экране и в базе не разъехались.
 */

import { MAX_LEVEL } from "./mascot";

/** Ровно то, что отдаёт `gamify.get_state` (ключ `xp` в /me и `progress` в /answer). */
export interface XpState {
  xp_total: number;
  level: number;
  max_level: number;
  is_max: boolean;
  level_floor_xp: number;
  next_level_xp: number | null;
  in_level: number;
  level_span: number;
  to_next: number | null;
  pct: number;
  streak_days: number;
  character: string;
  characters: string[];
  xp_per_achievement: number;
}

/** Прибавка за один ответ — `runner.answer` → `xp` (в /answer). */
export interface XpGain {
  gained: number;
  level: number | null;
  level_before: number | null;
  level_up: boolean;
  xp_total: number | null;
}

/** Пока ядро не ответило (или слоя 3 нет) — честный ноль, а не выдуманные цифры. */
export const EMPTY: XpState = {
  xp_total: 0, level: 1, max_level: MAX_LEVEL, is_max: false, level_floor_xp: 0,
  next_level_xp: null, in_level: 0, level_span: 0, to_next: null, pct: 0,
  streak_days: 0, character: "lingvi", characters: [], xp_per_achievement: 0,
};

export function plural(n: number, one: string, few: string, many: string): string {
  const m10 = n % 10, m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return one;
  if (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)) return few;
  return many;
}

const num = (n: number) => Math.round(n).toLocaleString("ru-RU");

/** «12 опытa» → «12 опыта»: слово «опыт» не склоняется по числу, а вот «очко» бы склонялось. */
export function xpLabel(n: number): string {
  return `${num(n)} ${plural(Math.round(n), "опыт", "опыта", "опыта")}`;
}

/** Прибавка на сводке подхода: «+47 опыта». Ноль показывать нечего. */
export function gainLabel(gain: number): string {
  return gain > 0 ? `+${xpLabel(gain)}` : "";
}

/**
 * Геометрия кольца: длина окружности и сдвиг штриха под долю `pct`.
 * Полное кольцо = offset 0, пустое = вся длина. Доля берётся ИЗ ЯДРА.
 */
export function ring(pct: number, r = 54): { len: number; offset: number } {
  const len = 2 * Math.PI * r;
  const clamped = Math.max(0, Math.min(100, Number.isFinite(pct) ? pct : 0));
  return { len: Math.round(len * 10) / 10, offset: Math.round(len * (1 - clamped / 100) * 10) / 10 };
}

/** Уровень для рисования маскота: зажат в границы героя (кривая ядра может меняться). */
export function mascotLevel(x: Partial<XpState> | null | undefined): number {
  const lv = Math.round(Number(x?.level ?? 1)) || 1;
  return Math.min(MAX_LEVEL, Math.max(1, lv));
}

/** «Уровень 4 из 12» — полная подпись (настройки, где место есть). */
export function levelLabel(x: XpState): string {
  return `Уровень ${x.level} из ${x.max_level || MAX_LEVEL}`;
}

/**
 * «4-й уровень» — короткая подпись рядом с именем героя в кабинете.
 * ⚠ Почему не полная: на 390 px строка «Кукуруза · уровень 12 из 12» рвётся, и «из 12»
 * уезжает одна на вторую строку (поймано скриншотом стенда). Потолок и так виден —
 * он стоит в настройках и в подписи «Максимальный уровень».
 */
export function levelShort(x: XpState): string {
  return `${x.level}-й уровень`;
}

/**
 * «До 5 уровня — 320 опыта» / на максимуме — «Максимальный уровень».
 * ⛔ Никакого «осталось чуть-чуть»: цифра всегда настоящая, из ядра.
 */
export function toNextLabel(x: XpState): string {
  if (x.is_max || x.to_next === null) return "Максимальный уровень — дальше некуда расти";
  return `До ${x.level + 1} уровня — ${xpLabel(x.to_next)}`;
}

/**
 * «8-й уровень · до 9-го ещё 3 200 опыта» — вторая строка героя в кабинете.
 * ⚠ Уровень уехал сюда со строки имени нарочно: рядом с именем он не влезал ни в каком
 * виде (стенд 390 px рвал и «уровень 12 из 12», и «12-й уровень»), а здесь шрифт мельче
 * и перенос выглядит как обычный текст, а не как обрубок заголовка.
 */
export function heroLine(x: XpState): string {
  if (x.is_max || x.to_next === null) return `${levelShort(x)} — максимум`;
  return `${levelShort(x)} · до ${x.level + 1}-го ещё ${xpLabel(x.to_next)}`;
}

/** «140 / 300» внутри текущего уровня — для мелкой строки под кольцом. */
export function inLevelLabel(x: XpState): string {
  if (x.is_max || !x.level_span) return `${num(x.xp_total)} всего`;
  return `${num(x.in_level)} / ${num(x.level_span)}`;
}

/** Что даёт награда (экран «Награды»): цену присылает ядро, а не витрина. */
export function rewardLabel(perAchievement: number): string {
  return perAchievement > 0 ? `+${xpLabel(perAchievement)} и отметку в этом списке` : "";
}

/** Серия дней в кабинете: «🔥 5 дней» / «🌱 начинаем» (⭐ считается по tz человека). */
export function streakLabel(days: number): string {
  return days > 0
    ? `🔥 ${days} ${plural(days, "день", "дня", "дней")}`
    : "🌱 начинаем";
}
